<template>
  <a-select
    :value="selectedValue"
    show-search
    allow-clear
    :size="size"
    :placeholder="placeholder"
    :filter-option="false"
    :default-active-first-option="false"
    class="device-search-select"
    @search="handleSearch"
    @change="handleChange"
    @dropdownVisibleChange="handleDropdownVisibleChange"
    @popupScroll="handlePopupScroll"
  >
    <a-select-option v-for="item in mergedOptions" :key="item.device_mac" :value="item.device_mac">
      {{ formatDeviceLabel(item) }}
    </a-select-option>
  </a-select>
</template>

<script>
export default {
  name: 'DeviceSearchSelect',
  props: {
    value: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: '請選擇機台'
    },
    size: {
      type: String,
      default: undefined
    },
    limit: {
      type: Number,
      default: 30
    },
    searchDebounce: {
      type: Number,
      default: 300
    },
    includeAllOption: {
      type: Boolean,
      default: false
    },
    allOptionLabel: {
      type: String,
      default: '全部'
    },
    allOptionValue: {
      type: String,
      default: 'all'
    }
  },
  data () {
    return {
      baseURL: this.GLOBAL.baseURL,
      basePort: this.GLOBAL.basePort,
      loading: false,
      options: [],
      currentPage: 0,
      totalPages: 1,
      currentSearch: '',
      requestToken: 0,
      searchTimer: null,
      internalValue: null,
      selectedOption: null
    }
  },
  computed: {
    selectedValue () {
      return this.value !== null && this.value !== undefined ? this.value : this.internalValue
    },
    mergedOptions () {
      const optionMap = new Map()

      this.options.forEach((item) => {
        optionMap.set(item.device_mac, item)
      })

      if (this.selectedOption && !optionMap.has(this.selectedOption.device_mac)) {
        optionMap.set(this.selectedOption.device_mac, this.selectedOption)
      }

      const optionList = Array.from(optionMap.values())

      if (this.includeAllOption) {
        optionList.unshift({
          device_mac: this.allOptionValue,
          device_name: this.allOptionLabel,
          city: ''
        })
      }

      return optionList.filter((item, index, arr) => arr.findIndex((target) => target.device_mac === item.device_mac) === index)
    },
    hasMorePages () {
      return this.currentPage < this.totalPages
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newValue) {
        if (!newValue) {
          this.internalValue = null
          this.selectedOption = null
          return
        }

        this.internalValue = newValue

        if (this.includeAllOption && newValue === this.allOptionValue) {
          this.selectedOption = {
            device_mac: this.allOptionValue,
            device_name: this.allOptionLabel,
            city: ''
          }
          return
        }

        const matchedOption = this.options.find((item) => item.device_mac === newValue)
        if (matchedOption) {
          this.selectedOption = matchedOption
        }
      }
    }
  },
  beforeDestroy () {
    clearTimeout(this.searchTimer)
  },
  methods: {
    formatDeviceLabel (item) {
      if (!item) {
        return ''
      }

      if (item.device_mac === this.allOptionValue) {
        return item.device_name
      }

      return item.city ? `${item.device_name} (${item.city})` : item.device_name
    },
    normalizeDevice (device) {
      return {
        device_mac: device.device_mac,
        device_name: device.device_name,
        city: device.city || device.location || ''
      }
    },
    mergeDeviceOptions (existingOptions, incomingOptions) {
      const optionMap = new Map()

      existingOptions.forEach((item) => {
        optionMap.set(item.device_mac, item)
      })

      incomingOptions.forEach((item) => {
        optionMap.set(item.device_mac, item)
      })

      return Array.from(optionMap.values())
    },
    fetchOptions ({ search = '', reset = false } = {}) {
      if (this.loading && !reset) {
        return
      }

      const requestToken = this.requestToken + 1
      const nextPage = reset ? 1 : this.currentPage + 1

      this.requestToken = requestToken
      this.loading = true

      this.axios
        .post(`${this.baseURL}${this.basePort}/device/deviceList`, {
          page: nextPage,
          limit: this.limit,
          search: search
        })
        .then((res) => {
          if (requestToken !== this.requestToken) {
            return
          }

          if (res.data.message !== 'success') {
            this.$message.error('獲取機台列表失敗')
            return
          }

          const incomingOptions = (res.data.data || []).map(this.normalizeDevice)

          this.options = reset ? incomingOptions : this.mergeDeviceOptions(this.options, incomingOptions)
          this.currentSearch = search

          if (res.data.pagination) {
            this.currentPage = Number(res.data.pagination.page) || nextPage
            this.totalPages = Number(res.data.pagination.totalPages) || 1
          } else {
            this.currentPage = 1
            this.totalPages = 1
          }

          if (this.value) {
            const matchedOption = this.options.find((item) => item.device_mac === this.value)
            if (matchedOption) {
              this.selectedOption = matchedOption
            }
          }
        })
        .catch((error) => {
          if (requestToken !== this.requestToken) {
            return
          }

          console.error('獲取機台列表失敗:', error)
          this.$message.error('獲取機台列表失敗，請稍後重試')
        })
        .finally(() => {
          if (requestToken === this.requestToken) {
            this.loading = false
          }
        })
    },
    handleSearch (value) {
      clearTimeout(this.searchTimer)

      this.searchTimer = setTimeout(() => {
        this.fetchOptions({
          search: (value || '').trim(),
          reset: true
        })
      }, this.searchDebounce)
    },
    handleChange (value) {
      const normalizedValue = value || null

      if (!normalizedValue) {
        this.internalValue = null
        this.selectedOption = null
        this.$emit('input', null)
        this.$emit('change', null, null)
        return
      }

      if (this.includeAllOption && normalizedValue === this.allOptionValue) {
        this.selectedOption = {
          device_mac: this.allOptionValue,
          device_name: this.allOptionLabel,
          city: ''
        }
      } else {
        this.selectedOption = this.mergedOptions.find((item) => item.device_mac === normalizedValue) || null
      }

      this.internalValue = normalizedValue
      this.$emit('input', normalizedValue)
      this.$emit('change', normalizedValue, this.selectedOption)
    },
    handleDropdownVisibleChange (open) {
      if (!open) {
        return
      }

      this.fetchOptions({ reset: true, search: this.currentSearch })
    },
    handlePopupScroll (event) {
      if (this.loading || !this.hasMorePages) {
        return
      }

      const target = event.target
      if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 24) {
        this.fetchOptions({ search: this.currentSearch, reset: false })
      }
    }
  }
}
</script>

<style scoped>
.device-search-select {
  width: 100%;
}
</style>
